/**
 * Shrink a picked product photo before it becomes a data: URL.
 *
 * Every product tile is shown at 600 px square, yet the admin form used to post
 * the file byte-for-byte: a 2 MB phone PNG became a ~3 MB base64 PUT, which a
 * slow venue uplink cannot finish inside the client's 30 s timeout. That is
 * why Parched's admins could not replace their photos (2026-10-13). Resizing
 * to a 1200 px long edge and re-encoding heavy PNGs as JPEG turns that into a
 * ~150 KB PUT without touching what the screen shows.
 *
 * `planDownscale` is the pure decision (tested); `fileToDataUrl` is the thin
 * canvas wrapper around it. GIFs are passed through so an animation is never
 * flattened to one frame.
 */

export const MAX_EDGE_PX = 1200
/** A photo under this many bytes is already cheap enough to post as-is. */
export const REENCODE_OVER_BYTES = 300_000
export const JPEG_QUALITY = 0.85

export interface PhotoFacts {
  width: number
  height: number
  type: string
  size: number
}

export interface DownscalePlan {
  width: number
  height: number
  mime: 'image/jpeg' | 'image/webp'
  quality: number
}

/** null = post the file untouched. */
export function planDownscale(f: PhotoFacts, maxEdge: number = MAX_EDGE_PX): DownscalePlan | null {
  if (f.type === 'image/gif') return null
  const longEdge = Math.max(f.width, f.height)
  const scale = longEdge > maxEdge ? maxEdge / longEdge : 1
  const needsResize = scale < 1
  const needsReencode = f.size > REENCODE_OVER_BYTES
  if (!needsResize && !needsReencode) return null
  return {
    width: Math.round(f.width * scale),
    height: Math.round(f.height * scale),
    mime: f.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
    quality: JPEG_QUALITY,
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.readAsDataURL(file)
  })
}

/**
 * The picked file as a data: URL, downscaled per `planDownscale`. Any failure
 * in decoding or drawing (an exotic format, a tainted canvas, an old WebView)
 * falls back to the untouched file — the pre-2026-10 behaviour — so a photo is
 * never lost to the optimisation.
 */
export async function fileToDataUrl(file: File, maxEdge: number = MAX_EDGE_PX): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file)
    try {
      const plan = planDownscale({ width: bitmap.width, height: bitmap.height, type: file.type, size: file.size }, maxEdge)
      if (!plan) return await readAsDataUrl(file)
      const canvas = document.createElement('canvas')
      canvas.width = plan.width
      canvas.height = plan.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return await readAsDataUrl(file)
      if (plan.mime === 'image/jpeg') {
        // JPEG has no alpha; a transparent PNG would otherwise come out black.
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, plan.width, plan.height)
      }
      ctx.drawImage(bitmap, 0, 0, plan.width, plan.height)
      const out = canvas.toDataURL(plan.mime, plan.quality)
      return out.startsWith('data:image/') ? out : await readAsDataUrl(file)
    } finally {
      bitmap.close()
    }
  } catch {
    return readAsDataUrl(file)
  }
}
